"use client";

/**
 * AnimatedCounter — unified spring-animated numeric display.
 *
 * Single source of truth for Plan v2, Brand v2, Niche v3.
 *
 * Promoted (Session 43) from forks:
 *   - PlanShared.AnimatedCounter        → API: { value, prefix, suffix, format: "integer"|"dollar"|"dollarK"|"percent" }
 *                                         IntersectionObserver inline, useSpring + .on("change") setState.
 *   - niche-v3/.../AnimatedCounter      → API: { from, to, duration, formatter, prefix, suffix, startOnView }
 *                                         framer-motion useInView + useTransform reactive display.
 *
 * Unified API supports BOTH:
 *   - new (Niche v3): `{ from, to, formatter, ... }`
 *   - legacy (Plan/Brand): `{ value, format: "integer"|... }` — `value` aliases `to`, `format` resolves to formatter.
 *
 * Reduced motion: jump to target instantly, no animation, no delay.
 * a11y: aria-live=off (silent during animation), aria-label=final value.
 */

import {
  motion,
  useInView,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
} from "framer-motion";
import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

export type AnimatedCounterFormat = "integer" | "dollar" | "dollarK" | "percent";

interface AnimatedCounterProps {
  /** Starting value. Default 0. */
  from?: number;
  /** Target value (preferred — new API). */
  to?: number;
  /** Legacy alias for `to` — Plan/Brand callers. If both set, `to` wins. */
  value?: number;
  /** Spring duration hint in seconds. Default 1.2. */
  duration?: number;
  /** Custom formatter function. Receives the rounded number, returns display string. */
  formatter?: (n: number) => string;
  /** Built-in format token (Plan/Brand legacy). Resolved into a formatter if `formatter` is not provided. */
  format?: AnimatedCounterFormat;
  /** Static prefix (e.g. "$"). Rendered outside formatter. */
  prefix?: string;
  /** Static suffix (e.g. "/mo"). Rendered outside formatter. */
  suffix?: string;
  /** Extra classes. */
  className?: string;
  /** Start animation only when scrolled into view. Default true. */
  startOnView?: boolean;
}

const intl = new Intl.NumberFormat("en-US");
const defaultFormatter = (n: number): string => intl.format(Math.round(n));

function resolveFormatter(
  format: AnimatedCounterFormat | undefined,
  custom: ((n: number) => string) | undefined,
): (n: number) => string {
  if (custom) return custom;
  if (!format) return defaultFormatter;
  switch (format) {
    case "dollar":
      // Lock locale to en-US — toLocaleString() without args picks system locale,
      // which differs between Node SSR and a non-en browser (NBSP vs comma) and
      // produces a React hydration mismatch on aria-label.
      return (n) => `$${intl.format(Math.round(n))}`;
    case "dollarK":
      return (n) => {
        const r = Math.round(n);
        return r >= 1000 ? `$${(r / 1000).toFixed(1)}K` : `$${r}`;
      };
    case "percent":
      return (n) => `${Math.round(n)}%`;
    case "integer":
    default:
      return defaultFormatter;
  }
}

export function AnimatedCounter({
  from = 0,
  to,
  value,
  duration = 1.2,
  formatter,
  format,
  prefix = "",
  suffix = "",
  className,
  startOnView = true,
}: AnimatedCounterProps) {
  const reduced = useReducedMotion();
  const ref = useRef<HTMLSpanElement>(null);
  // threshold:0.3 mirrors original Plan/Niche behavior.
  const inView = useInView(ref, { once: true, amount: 0.3 });

  // Resolve target — new API (`to`) wins, otherwise legacy `value`. Default 0 if neither.
  const target = to ?? value ?? 0;
  const fmt = resolveFormatter(format, formatter);

  // Spring tuned to roughly match `duration` at stiffness ≈ 100/duration.
  // damping 20 / mass 0.8 feels visually close to the legacy Plan curve.
  const stiffness = 120 / Math.max(0.5, duration);
  const motionValue = useMotionValue(reduced ? target : from);
  const spring = useSpring(motionValue, {
    stiffness,
    damping: 20,
    mass: 0.8,
  });
  const display = useTransform(
    spring,
    (v) => `${prefix}${fmt(v)}${suffix}`,
  );

  useEffect(() => {
    if (reduced) {
      motionValue.set(target);
      return;
    }
    const shouldStart = startOnView ? inView : true;
    if (shouldStart) {
      motionValue.set(target);
    }
  }, [reduced, inView, startOnView, target, motionValue]);

  const ariaLabel = `${prefix}${fmt(target)}${suffix}`;

  return (
    <motion.span
      ref={ref}
      className={cn("tabular-nums", className)}
      // role="img" makes aria-label legal on a span (ARIA 1.2 —
      // aria-label alone is prohibited on generic/paragraph elements).
      // "img" here is semantic "atomic visual presenting the final
      // value" rather than a graphic — SR reads the label as a whole.
      role="img"
      aria-label={ariaLabel}
      aria-live="off"
    >
      <motion.span aria-hidden>{display}</motion.span>
    </motion.span>
  );
}
